<template>
  <div class="orbit-container">
    <div class="orbit-interface">
      <!-- Header avec logo et bouton de connexion -->
      <div class="orbit-header">
        <div class="orbit-logo">
          <!--<div class="logo-icon">
            <i class="fas fa-satellite-dish"></i>
          </div>-->
          <div class="logo-icon">
            <img src="@/assets/orbit.png" alt="Heavenflix Logo" />
          </div>
          <div class="logo-text">
            <h1>Orbit</h1>
            <span>Canal vocal de Heavenflix</span>
          </div>
        </div>
        <button 
          @click="isConnected ? leaveOrbit() : joinOrbit()"
          :class="isConnected ? 'disconnect-btn' : 'connect-btn'"
          :disabled="isConnecting"
        >
          {{ isConnected ? 'Quitter' : 'Rejoindre' }}
        </button>
      </div>

      <!-- Panneau de contrôle -->
      <div class="control-panel">
        <h3>Control Panel</h3>
        
        <!-- Première rangée : Micro et Audio -->
        <div class="controls-row">
          <div class="control-part">
            <button 
              @click="toggleMicrophone" 
              :class="['status-btn', { 'active': !isMuted }]"
              :disabled="!isConnected || isAFK"
            >
              <i :class="isMuted ? 'fas fa-microphone-slash' : 'fas fa-microphone'"></i>
              Micro
            </button>
            <span :class="['status-btn-text']">{{ isAFK ? 'AFK' : (isMuted ? 'OFF' : 'ON') }}</span>
          </div>
          <div class="control-part">
            <button 
              @click="toggleDeafen" 
              :class="['status-btn', { 'active': !isDeafened }]"
              :disabled="!isConnected || isAFK"
            >
              <i :class="isDeafened ? 'fas fa-volume-mute' : 'fas fa-volume-up'"></i>
              Audio
            </button>
            <span :class="['status-btn-text']">{{ isAFK ? 'AFK' : (isDeafened ? 'OFF' : 'ON') }}</span>
          </div>
        </div>

        <!-- Contrôle du volume -->
        <div class="volume-control">
          <i class="fas fa-volume-down"></i>
          <div class="volume-slider-container">
            <input 
              type="range" 
              v-model="volume" 
              min="0" 
              max="100" 
              @input="adjustVolume"
              :disabled="!isConnected"
              class="slider"
            />
          </div>
          <span class="volume-value">{{ volume }}%</span>
        </div>

        <div class="spatial-mode-toggle">
          <div class="toggle-switch-container">
            <div 
              :class="['toggle-switch', { 'active': isAFK }]"
              @click="toggleAFK"
            ></div>
            <span>Mode spatial</span>
          </div>
          <div class="toggle-status-container">
            <span class="toggle-status">{{ isAFK ? 'ON' : 'OFF' }}</span>
            <i class="fa-solid fa-chevron-right"></i>
          </div>
        </div>
      </div>

      <!-- Liste des utilisateurs -->
      <div class="users-list">
        <h3>Utilisateurs connectés ({{ connectedUsers.length }})</h3>
        <div class="users-grid">
          <div 
            v-for="user in connectedUsers" 
            :key="user.userId"
            :class="['user-item', { 
              'speaking': isSpeaking(user.socketId)
            }]"
          >
            <div class="user-avatar">
              <i class="fas fa-user"></i>
            </div>
            <div class="user-info">
              <div class="user-name">
                {{ user.userName }}
                <span v-if="user.userId === currentUserId" class="you-tag">(vous)</span>
              </div>
              <div class="user-time">{{ formatJoinTime(user.joinTime) }}</div>
            </div>
            <div class="user-status">
              <i 
                v-if="getUserState(user.socketId).isAFK"
                class="fa-solid fa-user-astronaut"
                title="AFK"
              ></i>
              <i 
                v-if="getUserState(user.socketId).isMuted"
                class="fas fa-microphone-slash"
                title="Micro coupé"
              ></i>
              <i 
                v-if="getUserState(user.socketId).isDeafened"
                class="fas fa-volume-mute"
                title="Audio coupé"
              ></i>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Notifications -->
    <div class="notifications">
      <div 
        v-for="notification in notifications" 
        :key="notification.id"
        :class="['notification', notification.type]"
      >
        {{ notification.message }}
      </div>
    </div>
  </div>
</template>

<script>
import { io } from 'socket.io-client'

export default {
  name: 'OrbitContainer',
  data() {
    return {
      // État de connexion
      isConnected: false,
      isConnecting: false,
      socket: null,
      
      // Données utilisateur
      currentUserId: localStorage.getItem('userId'),
      currentUserName: localStorage.getItem('userName') || 'Utilisateur',
      connectedUsers: [],
      
      // Contrôles audio
      isMuted: false,
      isDeafened: false,
      isAFK: false,
      volume: 50,
      spatialMode: false,
      localStream: null,
      processedLocalStream: null, // Stream traité par Web Audio API
      audioContext: null,
      peerConnections: new Map(), // socketId -> RTCPeerConnection
      remoteStreams: new Map(),   // socketId -> MediaStream
      speakingUsers: new Set(),   // Utilisateurs qui parlent actuellement
      speakingTimers: new Map(),  // Timers pour maintenir le halo vert
      userStates: new Map(),      // États des utilisateurs (muted, deafened, afk)
      
      // Notifications
      notifications: [],
      
      // Configuration WebRTC SIMPLE
      rtcConfig: {
        iceServers: [
          { urls: 'stun:stun.l.google.com:19302' },
          { urls: 'stun:stun1.l.google.com:19302' }
        ]
      },
      
      // API
      link: "https://api.heavenflix.fr"
    }
  },
  computed: {
    connectionStatus() {
      if (this.isConnecting) return 'connecting'
      if (this.isConnected) return 'connected'
      return 'disconnected'
    },
    connectionText() {
      if (this.isConnecting) return 'Connexion en cours...'
      if (this.isConnected) return 'Connecté à Orbit'
      return 'Non connecté'
    }
  },
  async mounted() {
    console.log('🛰️ Orbit: Composant monté, début de l\'initialisation')
    console.log('🛰️ Orbit: UserId:', this.currentUserId)
    console.log('🛰️ Orbit: UserName:', this.currentUserName)
    
    // Vérifier la compatibilité WebRTC
    this.checkWebRTCSupport()
    
    await this.checkAccess()
    this.initializeSocket()
    
    // Demander la liste des utilisateurs connectés après un court délai
    setTimeout(() => {
      if (this.socket?.connected) {
        this.socket.emit('get-orbit-users')
        console.log('🛰️ Orbit: Demande de la liste des utilisateurs connectés')
      }
    }, 1000)
  },
  beforeUnmount() {
    this.leaveOrbit()
  },
  methods: {
    checkWebRTCSupport() {
      console.log('🛰️ Orbit: Vérification de la compatibilité WebRTC...')
      
      // Vérifier RTCPeerConnection
      if (!window.RTCPeerConnection) {
        console.error('🛰️ Orbit: RTCPeerConnection non supporté')
        this.showNotification('error', 'Votre navigateur ne supporte pas WebRTC. Veuillez utiliser Safari sur iOS.')
        return false
      }
      
      // Vérifier getUserMedia
      const hasGetUserMedia = !!(navigator.mediaDevices?.getUserMedia || 
                                navigator.getUserMedia || 
                                navigator.webkitGetUserMedia || 
                                navigator.mozGetUserMedia)
      
      if (!hasGetUserMedia) {
        console.error('🛰️ Orbit: getUserMedia non supporté')
        this.showNotification('error', 'Accès au microphone non supporté. Veuillez utiliser Safari sur iOS.')
        return false
      }
      
      console.log('🛰️ Orbit: ✅ WebRTC supporté')
      
      // Afficher des conseils spécifiques pour iOS
      if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
        console.log('🛰️ Orbit: Détection iOS - conseils d\'utilisation')
        if (!navigator.userAgent.includes('Safari') && navigator.userAgent.includes('Chrome')) {
          this.showNotification('warning', 'Pour une meilleure expérience sur iOS, utilisez Safari plutôt que Chrome')
        }
      }
      
      return true
    },
    
    async checkAccess() {
      console.log('🛰️ Orbit: Vérification de l\'accès...')
      try {
        const token = localStorage.getItem('token')
        console.log('🛰️ Orbit: Token présent:', !!token)
        
        const response = await fetch(`${this.link}/orbit/access/${this.currentUserId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
        
        console.log('🛰️ Orbit: Réponse API access:', response.status, response.ok)
        
        if (!response.ok) {
          const errorText = await response.text()
          console.error('🛰️ Orbit: Erreur API access:', errorText)
          throw new Error('Accès refusé à Orbit')
        }
        
        const data = await response.json()
        console.log('🛰️ Orbit: Données utilisateur reçues:', data)
        this.currentUserName = data.user.name
      } catch (error) {
        console.error('🛰️ Orbit: Erreur checkAccess:', error)
        this.showNotification('error', 'Erreur d\'accès à Orbit: ' + error.message)
        this.$router.push('/movies')
      }
    },
    
    initializeSocket() {
      console.log('🛰️ Orbit: Initialisation de Socket.io vers:', this.link)
      this.socket = io(this.link, {
        transports: ['websocket'],
        forceNew: true
      })
      
      this.socket.on('connect', () => {
        console.log('🛰️ Orbit: Socket connecté avec ID:', this.socket.id)
      })
      
      this.socket.on('disconnect', () => {
        console.log('🛰️ Orbit: Socket déconnecté')
      })
      
      this.socket.on('connect_error', (error) => {
        console.error('🛰️ Orbit: Erreur de connexion Socket:', error)
      })
      
      // Événements Socket.io pour Orbit
      this.socket.on('orbit-users-list', (users) => {
        console.log('🛰️ Orbit: Liste des utilisateurs reçue:', users)
        this.connectedUsers = users
      })
      
      // Demander la liste des utilisateurs connectés dès la connexion socket
      this.socket.on('connect', () => {
        console.log('🛰️ Orbit: Socket connecté avec ID:', this.socket.id)
        // Demander immédiatement la liste des utilisateurs connectés
        this.socket.emit('get-orbit-users')
      })
      
      this.socket.on('user-joined-orbit', (user) => {
        console.log('🛰️ Orbit: Utilisateur rejoint:', user)
        
        // ÉVITER LES DOUBLONS : vérifier si l'utilisateur n'est pas déjà présent
        const existingUser = this.connectedUsers.find(u => u.socketId === user.socketId)
        if (!existingUser) {
          this.connectedUsers.push(user)
          //this.showNotification('info', `${user.userName} a rejoint Orbit`)
          
          // Jouer un son de notification pour un nouvel arrivant
          this.playNotificationSound('join')
          
          // Créer une connexion WebRTC vers ce nouvel utilisateur (seulement si c'est pas nous)
          if (this.localStream && user.socketId !== this.socket.id) {
            this.createWebRTCConnection(user.socketId)
          }
        } else {
          console.log('🛰️ Orbit: Utilisateur déjà présent, ignoré')
        }
      })
      
      this.socket.on('user-left-orbit', (user) => {
        console.log('🛰️ Orbit: Utilisateur parti:', user)
        this.connectedUsers = this.connectedUsers.filter(u => u.userId !== user.userId)
        //this.showNotification('info', `${user.userName} a quitté Orbit`)
        
        // Jouer un son de notification pour un départ
        this.playNotificationSound('leave')
        
        // Nettoyer les connexions WebRTC
        this.cleanupPeerConnection(user.socketId)
      })
      
      // Événements WebRTC SIMPLES avec protection contre les doublons
      this.socket.on('webrtc-offer', async (data) => {
        console.log('🛰️ WebRTC: Offer reçue de:', data.fromSocketId)
        
        // Éviter de traiter une offre de nous-même
        if (data.fromSocketId === this.socket.id) {
          console.log('🛰️ WebRTC: Offre de nous-même ignorée')
          return
        }
        
        await this.handleWebRTCOffer(data.offer, data.fromSocketId)
      })
      
      this.socket.on('webrtc-answer', async (data) => {
        console.log('🛰️ WebRTC: Answer reçue de:', data.fromSocketId)
        
        // Vérifier que la connexion existe et est dans le bon état
        const peerConnection = this.peerConnections.get(data.fromSocketId)
        if (!peerConnection) {
          console.log('🛰️ WebRTC: Pas de connexion pour cette réponse, ignorée')
          return
        }
        
        if (peerConnection.signalingState !== 'have-local-offer') {
          console.log('🛰️ WebRTC: État incorrect pour réponse:', peerConnection.signalingState)
          return
        }
        
        await this.handleWebRTCAnswer(data.answer, data.fromSocketId)
      })
      
      this.socket.on('webrtc-ice-candidate', async (data) => {
        console.log('🛰️ WebRTC: ICE candidate reçu de:', data.fromSocketId)
        await this.handleICECandidate(data.candidate, data.fromSocketId)
      })
      
      // Événements pour la synchronisation des états utilisateurs
      this.socket.on('user-state-changed', (data) => {
        console.log('🛰️ Orbit: État utilisateur changé:', data)
        this.userStates.set(data.socketId, {
          isMuted: data.isMuted,
          isDeafened: data.isDeafened,
          isAFK: data.isAFK
        })
      })
    },
    
    async joinOrbit() {
      console.log('🛰️ Orbit: Tentative de rejoindre Orbit')
      if (this.isConnecting || this.isConnected) {
        console.log('🛰️ Orbit: Déjà en cours de connexion ou connecté')
        return
      }
      
      this.isConnecting = true
      console.log('🛰️ Orbit: État de connexion -> connecting')
      
      try {
        console.log('🛰️ Orbit: Demande d\'autorisation microphone...')
        
        // Vérification de compatibilité pour iOS/Safari/Chrome mobile
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          // Fallback pour les navigateurs plus anciens ou iOS
          if (navigator.getUserMedia || navigator.webkitGetUserMedia || navigator.mozGetUserMedia) {
            const getUserMedia = navigator.getUserMedia || navigator.webkitGetUserMedia || navigator.mozGetUserMedia
            this.localStream = await new Promise((resolve, reject) => {
              getUserMedia.call(navigator, { audio: true }, resolve, reject)
            })
          } else {
            throw new Error('Votre navigateur ne supporte pas l\'accès au microphone. Veuillez utiliser Safari sur iOS ou mettre à jour votre navigateur.')
          }
        } else {
          // Configuration moderne pour les navigateurs compatibles
          this.localStream = await navigator.mediaDevices.getUserMedia({ 
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
              sampleRate: 48000,
              channelCount: 1
            }
          })
        }
        
        console.log('🛰️ Orbit: ✅ Microphone obtenu, stream original:', this.localStream)
        
        // Traiter le stream avec Web Audio API pour un contrôle total
        this.processLocalStream()
        
        // Créer un élément audio local MUET pour améliorer l'annulation d'écho
        const localAudioElement = document.createElement('audio')
        localAudioElement.id = 'local-orbit-audio'
        localAudioElement.srcObject = this.localStream
        localAudioElement.muted = true // TRÈS IMPORTANT
        localAudioElement.style.display = 'none'
        document.body.appendChild(localAudioElement)
        
        console.log("🛰️ Orbit: ✅ Élément audio local muet créé pour l'annulation d'écho")
        
        // Rejoindre le channel
        this.socket.emit('join-orbit', {
          userId: this.currentUserId,
          userName: this.currentUserName,
          token: localStorage.getItem('token')
        })
        
        this.isConnected = true
        this.isConnecting = false
        
        //this.showNotification('success', 'Vous avez rejoint Orbit !')
        
        // Créer les connexions WebRTC avec tous les utilisateurs déjà présents
        setTimeout(() => {
          this.connectedUsers.forEach(user => {
            if (user.socketId !== this.socket.id) {
              console.log('🛰️ Orbit: Création connexion WebRTC avec:', user.userName, 'socketId:', user.socketId)
              this.createWebRTCConnection(user.socketId)
            }
          })
        }, 500)
        
      } catch (error) {
        console.error('🛰️ Orbit: ❌ Erreur joinOrbit:', error)
        this.isConnecting = false
        this.showNotification('error', 'Erreur: ' + error.message)
      }
    },
    
    leaveOrbit() {
      if (!this.isConnected) return
      
      // Arrêter le stream local
      if (this.localStream) {
        this.localStream.getTracks().forEach(track => track.stop())
        this.localStream = null
      }
      
      // Fermer le contexte audio
      if (this.audioContext) {
        this.audioContext.close()
        this.audioContext = null
        this.processedLocalStream = null
      }
      
      // Fermer toutes les connexions WebRTC
      this.peerConnections.forEach((pc, socketId) => {
        pc.close()
        this.cleanupPeerConnection(socketId)
      })
      this.peerConnections.clear()
      this.remoteStreams.clear()
      
      // Supprimer l'élément audio local
      const localAudioElement = document.getElementById('local-orbit-audio')
      if (localAudioElement) {
        document.body.removeChild(localAudioElement)
      }
      
      // Supprimer tous les éléments audio distants
      document.querySelectorAll('audio[id^="orbit-audio-"]').forEach(audio => {
        document.body.removeChild(audio)
      })
      
      // Quitter le channel
      this.socket.emit('leave-orbit')
      
      this.isConnected = false
      this.connectedUsers = []

      //this.showNotification('info', 'Vous avez quitté Orbit')
    },
    
    // ========== MÉTHODES WEBRTC PROPRES ==========
    
    async createWebRTCConnection(targetSocketId) {
      console.log('🛰️ WebRTC: Création connexion vers:', targetSocketId)
      
      // Éviter les connexions vers nous-même
      if (targetSocketId === this.socket.id) {
        console.log('🛰️ WebRTC: Tentative de connexion vers soi-même ignorée')
        return
      }
      
      // Éviter les doublons de connexion
      if (this.peerConnections.has(targetSocketId)) {
        console.log('🛰️ WebRTC: Connexion déjà existante vers:', targetSocketId)
        return
      }
      
      const peerConnection = new RTCPeerConnection(this.rtcConfig)
      this.peerConnections.set(targetSocketId, peerConnection)
      
      const streamToSend = this.processedLocalStream || this.localStream
      
      // Ajouter notre stream audio local traité
      streamToSend.getTracks().forEach(track => {
        peerConnection.addTrack(track, streamToSend)
        console.log('🛰️ WebRTC: Track local ajouté:', track.kind)
      })
      
      // Écouter les streams distants
      peerConnection.ontrack = (event) => {
        console.log('🛰️ WebRTC: ✅ Stream distant reçu de:', targetSocketId)
        const remoteStream = event.streams[0]
        this.remoteStreams.set(targetSocketId, remoteStream)
        this.playRemoteAudio(remoteStream, targetSocketId)
      }
      
      // Gestion des candidats ICE
      peerConnection.onicecandidate = (event) => {
        if (event.candidate) {
          this.socket.emit('webrtc-ice-candidate', {
            candidate: event.candidate,
            targetSocketId: targetSocketId
          })
        }
      }
      
      // État de connexion
      peerConnection.onconnectionstatechange = () => {
        console.log(`🛰️ WebRTC: État connexion ${targetSocketId}:`, peerConnection.connectionState)
      }
      
      // Créer et envoyer l'offre
      const offer = await peerConnection.createOffer()
      await peerConnection.setLocalDescription(offer)
      
      this.socket.emit('webrtc-offer', {
        offer: offer,
        targetSocketId: targetSocketId
      })
    },
    
    async handleWebRTCOffer(offer, fromSocketId) {
      console.log('🛰️ WebRTC: Traitement offre de:', fromSocketId)
      
      // Éviter de traiter une offre de nous-même
      if (fromSocketId === this.socket.id) {
        console.log('🛰️ WebRTC: Offre de nous-même ignorée')
        return
      }
      
      // Si une connexion existe déjà, la fermer avant d'en créer une nouvelle
      if (this.peerConnections.has(fromSocketId)) {
        console.log('🛰️ WebRTC: Connexion existante fermée pour:', fromSocketId)
        const existingPC = this.peerConnections.get(fromSocketId)
        existingPC.close()
        this.peerConnections.delete(fromSocketId)
      }
      
      const peerConnection = new RTCPeerConnection(this.rtcConfig)
      this.peerConnections.set(fromSocketId, peerConnection)
      
      const streamToSend = this.processedLocalStream || this.localStream
      
      // Ajouter notre stream traité
      streamToSend.getTracks().forEach(track => {
        peerConnection.addTrack(track, streamToSend)
      })
      
      // Écouter les streams distants
      peerConnection.ontrack = (event) => {
        console.log('🛰️ WebRTC: ✅ Stream distant reçu de:', fromSocketId)
        const remoteStream = event.streams[0]
        this.remoteStreams.set(fromSocketId, remoteStream)
        this.playRemoteAudio(remoteStream, fromSocketId)
      }
      
      // Gestion ICE
      peerConnection.onicecandidate = (event) => {
        if (event.candidate) {
          this.socket.emit('webrtc-ice-candidate', {
            candidate: event.candidate,
            targetSocketId: fromSocketId
          })
        }
      }
      
      // État de connexion
      peerConnection.onconnectionstatechange = () => {
        console.log(`🛰️ WebRTC: État connexion ${fromSocketId}:`, peerConnection.connectionState)
      }
      
      await peerConnection.setRemoteDescription(offer)
      const answer = await peerConnection.createAnswer()
      await peerConnection.setLocalDescription(answer)
      
      this.socket.emit('webrtc-answer', {
        answer: answer,
        targetSocketId: fromSocketId
      })
    },
    
    async handleWebRTCAnswer(answer, fromSocketId) {
      console.log('🛰️ WebRTC: Traitement réponse de:', fromSocketId)
      const peerConnection = this.peerConnections.get(fromSocketId)
      if (peerConnection) {
        await peerConnection.setRemoteDescription(answer)
      }
    },
    
    async handleICECandidate(candidate, fromSocketId) {
      const peerConnection = this.peerConnections.get(fromSocketId)
      if (peerConnection) {
        try {
          await peerConnection.addIceCandidate(candidate)
        } catch (error) {
          console.error('🛰️ WebRTC: Erreur ICE candidate:', error)
        }
      }
    },
    
    playRemoteAudio(remoteStream, socketId) {
      console.log('🛰️ Audio: Lecture stream distant de:', socketId)
      
      // Créer l'élément audio pour ce stream distant
      let audioElement = document.getElementById(`orbit-audio-${socketId}`)
      if (!audioElement) {
        audioElement = document.createElement('audio')
        audioElement.id = `orbit-audio-${socketId}`
        audioElement.autoplay = true
        audioElement.volume = this.volume / 100
        audioElement.style.display = 'none'
        
        // IMPORTANT pour Firefox : définir les attributs avant d'ajouter au DOM
        audioElement.controls = false
        audioElement.muted = false
        audioElement.playsInline = true
        
        document.body.appendChild(audioElement)
        console.log('🛰️ Audio: ✅ Élément audio créé pour:', socketId)
      }
      
      // IMPORTANT: Assigner le stream distant (pas le local!)
      audioElement.srcObject = remoteStream
      
      // Configurer la détection de voix pour cet utilisateur
      this.setupVoiceActivity(socketId, remoteStream)
      
      // Forcer la lecture pour Firefox
      const playPromise = audioElement.play()
      if (playPromise !== undefined) {
        playPromise.then(() => {
          console.log('🛰️ Audio: ✅ Lecture démarrée pour:', socketId)
        }).catch(err => {
          console.error('🛰️ Audio: ❌ Erreur lecture:', err)
          // Essayer une fois de plus après un délai
          setTimeout(() => {
            audioElement.play().catch(e => console.error('🛰️ Audio: Échec retry:', e))
          }, 1000)
        })
      }
      
      console.log('🛰️ Audio: ✅ Stream assigné à l\'élément audio')
    },
    
    cleanupPeerConnection(socketId) {
      const peerConnection = this.peerConnections.get(socketId)
      if (peerConnection) {
        peerConnection.close()
        this.peerConnections.delete(socketId)
      }
      
      this.remoteStreams.delete(socketId)
      
      // Nettoyer la détection de voix et les timers
      this.speakingUsers.delete(socketId)
      if (this.speakingTimers.has(socketId)) {
        clearTimeout(this.speakingTimers.get(socketId))
        this.speakingTimers.delete(socketId)
      }
      
      // Nettoyer l'état de l'utilisateur
      this.userStates.delete(socketId)
      
      const audioElement = document.getElementById(`orbit-audio-${socketId}`)
      if (audioElement) {
        document.body.removeChild(audioElement)
      }
      
      console.log('🛰️ WebRTC: ✅ Connexion nettoyée pour:', socketId)
    },
    
    
    toggleMicrophone() {
      console.log('🛰️ Orbit: Toggle microphone, état actuel muted:', this.isMuted)
      if (!this.localStream || this.isAFK) {
        console.error('🛰️ Orbit: Pas de stream local ou en mode AFK')
        return
      }
      
      const audioTrack = this.localStream.getAudioTracks()[0]
      if (audioTrack) {
        this.isMuted = !this.isMuted
        audioTrack.enabled = !this.isMuted
        
        console.log('🛰️ Orbit: Microphone', this.isMuted ? 'coupé' : 'activé')
        console.log('🛰️ Orbit: Track enabled:', audioTrack.enabled)
        
        // Diffuser le changement d'état
        this.broadcastUserState()
        
        //this.showNotification('info', this.isMuted ? 'Micro coupé' : 'Micro activé')
      } else {
        console.error('🛰️ Orbit: Pas de track audio trouvé')
      }
    },
    
    toggleDeafen() {
      if (this.isAFK) return
      
      this.isDeafened = !this.isDeafened
      
      // Couper/remettre le son de tous les éléments audio distants
      document.querySelectorAll('audio[id^="orbit-audio-"]').forEach(audio => {
        audio.muted = this.isDeafened
      })
      
      // Diffuser le changement d'état
      this.broadcastUserState()

      //this.showNotification('info', this.isDeafened ? 'Audio coupé' : 'Audio rétabli')
    },
    
    toggleAFK() {
      this.isAFK = !this.isAFK
      
      if (this.isAFK) {
        // Mode AFK : couper micro et son
        if (this.localStream) {
          const audioTrack = this.localStream.getAudioTracks()[0]
          if (audioTrack) {
            audioTrack.enabled = false
          }
        }
        
        // Couper tous les sons distants
        document.querySelectorAll('audio[id^="orbit-audio-"]').forEach(audio => {
          audio.muted = true
        })
        
        //this.showNotification('warning', 'Mode AFK activé')
      } else {
        // Sortir du mode AFK : restaurer les états précédents
        if (this.localStream && !this.isMuted) {
          const audioTrack = this.localStream.getAudioTracks()[0]
          if (audioTrack) {
            audioTrack.enabled = true
          }
        }
        
        // Restaurer le son si pas en sourdine
        if (!this.isDeafened) {
          document.querySelectorAll('audio[id^="orbit-audio-"]').forEach(audio => {
            audio.muted = false
          })
        }
        
        //this.showNotification('info', 'Mode AFK désactivé')
      }
      
      // Diffuser le changement d'état
      this.broadcastUserState()
    },
    
    // Diffuser notre état aux autres utilisateurs
    broadcastUserState() {
      if (this.socket && this.isConnected) {
        this.socket.emit('user-state-change', {
          isMuted: this.isMuted,
          isDeafened: this.isDeafened,
          isAFK: this.isAFK
        })
        console.log('🛰️ Orbit: État diffusé:', { isMuted: this.isMuted, isDeafened: this.isDeafened, isAFK: this.isAFK })
      }
    },
    
    adjustVolume() {
      console.log('🛰️ Orbit: Ajustement volume à:', this.volume)
      // Appliquer le volume à tous les éléments audio distants
      const audioElements = document.querySelectorAll('audio[id^="orbit-audio-"]')
      console.log('🛰️ Orbit: Nombre d\'éléments audio trouvés:', audioElements.length)
      audioElements.forEach(audio => {
        audio.volume = this.volume / 100
        console.log('🛰️ Orbit: Volume ajusté pour:', audio.id, 'à', audio.volume)
      })
    },
    
    formatJoinTime(time) {
      const diff = Date.now() - new Date(time).getTime()
      const minutes = Math.floor(diff / 60000)
      
      if (minutes < 1) return 'à l\'instant'
      if (minutes === 1) return 'depuis 1 minute'
      if (minutes < 60) return `depuis ${minutes} minutes`
      
      const hours = Math.floor(minutes / 60)
      if (hours === 1) return 'depuis 1 heure'
      return `depuis ${hours} heures`
    },
    
    getUserStatusIcon(user) {
      if (user.userId === this.currentUserId) return 'fas fa-crown'
      return 'fas fa-circle'
    },
    
    getUserStatusText(user) {
      if (user.userId === this.currentUserId) return 'Vous'
      return 'Connecté'
    },
    
    getUserState(socketId) {
      // Pour l'utilisateur actuel
      if (socketId === this.socket?.id) {
        return {
          isMuted: this.isMuted,
          isDeafened: this.isDeafened,
          isAFK: this.isAFK
        }
      }
      // Pour les autres utilisateurs
      return this.userStates.get(socketId) || { isMuted: false, isDeafened: false, isAFK: false }
    },
    
    isSpeaking(socketId) {
      return this.speakingUsers.has(socketId)
    },
    
    showNotification(type, message) {
      const notification = { type, message }
      this.notifications.push(notification)
      
      // Supprimer après 3 secondes
      setTimeout(() => {
        const index = this.notifications.indexOf(notification)
        if (index > -1) this.notifications.splice(index, 1)
      }, 3000)
    },
    
    processLocalStream() {
      if (!this.localStream) return
      
      try {
        this.audioContext = new (window.AudioContext || window.webkitAudioContext)()
        
        // Pour iOS, le contexte audio peut être suspendu au démarrage
        if (this.audioContext.state === 'suspended') {
          console.log('🛰️ Web Audio: Contexte suspendu, tentative de reprise...')
          this.audioContext.resume().then(() => {
            console.log('🛰️ Web Audio: Contexte audio repris')
          }).catch(err => {
            console.error('🛰️ Web Audio: Impossible de reprendre le contexte:', err)
          })
        }
        
        const source = this.audioContext.createMediaStreamSource(this.localStream)
        
        // Créer une destination de stream
        const destination = this.audioContext.createMediaStreamDestination()
        
        // Connecter la source à la destination (sans traitement pour l'instant)
        source.connect(destination)
        
        // Stocker le stream traité
        this.processedLocalStream = destination.stream
        console.log('🛰️ Web Audio: Stream local traité et prêt')
      } catch (error) {
        console.error('🛰️ Web Audio: Erreur lors du traitement du stream:', error)
        // En cas d'erreur, utiliser le stream original
        this.processedLocalStream = this.localStream
      }
    },
    
    // Jouer un son de notification
    playNotificationSound(type) {
      try {
        // Créer un contexte audio pour les sons
        const audioContext = new (window.AudioContext || window.webkitAudioContext)()
        
        // Pour iOS, vérifier l'état du contexte
        if (audioContext.state === 'suspended') {
          audioContext.resume().then(() => {
            this.createNotificationSound(audioContext, type)
          }).catch(() => {
            console.log('🔊 Son de notification silencieux (contexte suspendu)')
          })
        } else {
          this.createNotificationSound(audioContext, type)
        }
      } catch (error) {
        console.error('🔊 Erreur lors du son de notification:', error)
      }
    },
    
    createNotificationSound(audioContext, type) {
      const oscillator = audioContext.createOscillator()
      const gainNode = audioContext.createGain()
      
      oscillator.connect(gainNode)
      gainNode.connect(audioContext.destination)
      
      if (type === 'join') {
        // Son ascendant pour les arrivées (Do-Mi-Sol)
        oscillator.frequency.setValueAtTime(261.63, audioContext.currentTime) // Do
        oscillator.frequency.setValueAtTime(329.63, audioContext.currentTime + 0.1) // Mi
        oscillator.frequency.setValueAtTime(392.00, audioContext.currentTime + 0.2) // Sol
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime)
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3)
        oscillator.start(audioContext.currentTime)
        oscillator.stop(audioContext.currentTime + 0.3)
      } else if (type === 'leave') {
        // Son descendant pour les départs (Sol-Mi-Do)
        oscillator.frequency.setValueAtTime(392.00, audioContext.currentTime) // Sol
        oscillator.frequency.setValueAtTime(329.63, audioContext.currentTime + 0.1) // Mi
        oscillator.frequency.setValueAtTime(261.63, audioContext.currentTime + 0.2) // Do
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime)
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3)
        oscillator.start(audioContext.currentTime)
        oscillator.stop(audioContext.currentTime + 0.3)
      }
      
      console.log(`🔊 Son de notification joué: ${type}`)
    },
    
    // Détecter si un utilisateur parle
    setupVoiceActivity(socketId, remoteStream) {
      try {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)()
        const analyser = audioContext.createAnalyser()
        const source = audioContext.createMediaStreamSource(remoteStream)
        
        source.connect(analyser)
        analyser.fftSize = 256
        
        const bufferLength = analyser.frequencyBinCount
        const dataArray = new Uint8Array(bufferLength)
        
        const checkVoiceActivity = () => {
          analyser.getByteFrequencyData(dataArray)
          
          // Calculer le niveau audio moyen
          const average = dataArray.reduce((sum, value) => sum + value, 0) / bufferLength
          
          // Seuil de détection de voix (ajustable)
          const threshold = 20
          
          if (average > threshold) {
            if (!this.speakingUsers.has(socketId)) {
              this.speakingUsers.add(socketId)
              console.log(`🎤 ${socketId} commence à parler`)
            }
            
            // Réinitialiser ou créer le timer pour maintenir l'affichage
            if (this.speakingTimers.has(socketId)) {
              clearTimeout(this.speakingTimers.get(socketId))
            }
            
            // Timer pour maintenir le halo pendant au moins 1 seconde
            const timer = setTimeout(() => {
              this.speakingUsers.delete(socketId)
              this.speakingTimers.delete(socketId)
              console.log(`🔇 ${socketId} arrête de parler (timer)`)
            }, 1000)
            
            this.speakingTimers.set(socketId, timer)
          }
          
          // Continuer l'analyse
          requestAnimationFrame(checkVoiceActivity)
        }
        
        checkVoiceActivity()
      } catch (error) {
        console.error('🎤 Erreur lors de la configuration de la détection vocale:', error)
        // En cas d'erreur, pas de détection vocale mais ça fonctionne quand même
      }
    },
  }
}
</script>

<style scoped lang="scss">
$bg-color: #0f0f23;
$surface-color: rgba(28, 35, 64, 0.6);
$primary-accent: #00d9a3;
$secondary-accent: #7c3aed;
$error-color: #dc2626;
$text-primary: #ffffff;
$text-secondary: #94a3b8;
$border-color: rgba(59, 130, 246, 0.2);
$button-bg: rgba(30, 41, 82, 0.4);
$button-hover-bg: rgba(59, 130, 246, 0.1);
$accent-color: #00e0b8;
$back-color: #0c0a14;
$success-color: #00e0b8;
$warning-color: #f59e0b;
$info-color: #3b82f6;

.orbit-container {
  background-color: $bg-color;
  /*background-image: 
    radial-gradient(circle at 20% 50%, rgba(124, 58, 237, 0.08) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(59, 130, 246, 0.12) 0%, transparent 50%),
    radial-gradient(circle at 40% 80%, rgba(0, 217, 163, 0.06) 0%, transparent 50%);*/
  background: linear-gradient(135deg, #0C0F2A 0%, #07081D 95%, #0E0926 100%);
  min-height: 100vh;
  color: $text-primary;
  font-family: 'Inter', 'Segoe UI', system-ui, sans-serif;
  display: flex;
  align-items: center;
  justify-content: center;
}

.disconnect-btn {
    background: linear-gradient(90deg, #B4464E, #691C30);
    color: white;
    border: none;
    border-radius: 12px;
    padding: 8px 20px;
    font-weight: 600;
    font-size: 13px;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 2px 8px rgba(220, 38, 38, 0.3);
    border: 1px solid #F96F73;
    
    &:hover {
      box-shadow: 0 4px 12px rgba(220, 38, 38, 0.4);
    }
  }

.orbit-interface {
  width: 40%;
  //background: rgba(16, 18, 34, 0.35);
  border-radius: 24px;
  background: linear-gradient(135deg, #0F1732 5%, #0A0B21 30%);
  border: 1px solid #13234E;
  padding: 24px;
  backdrop-filter: blur(50px);
  -webkit-backdrop-filter: blur(50px);
  box-shadow: 
    0 12px 40px rgba(0, 0, 0, 0.5),
    inset 0 1px 1px rgba(255, 255, 255, 0.08);
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.orbit-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  .orbit-logo {
    display: flex;
    align-items: center;
    gap: 12px;
    
    .logo-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      
      img {
        width: 50px;
        height: 50px;
      }
    }
    
    .logo-text {
      h1 {
        font-size: 22px;
        font-weight: 700;
        margin: 0;
        color: $text-primary;
      }
      
      span {
        font-size: 14px;
        color: $text-secondary;
        font-weight: 400;
      }
    }
  }
  
  .connect-btn {
    background: linear-gradient(90deg, #149387 0%, #076556 100%);
    border: 1px solid #1EF8EB;
    color: white;
    border-radius: 15px;
    padding: 10px 16px;
    font-weight: 600;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.2s ease;
    
    &:hover:not(:disabled) {
      box-shadow: 0 4px 12px rgba(0, 217, 163, 0.3);
    }
    
    &:disabled {
      background: $text-secondary;
      cursor: not-allowed;
    }
  }
}

.control-panel {
  //background: rgba(10, 12, 28, 0.4);
  border-radius: 16px;
  padding: 20px;
  background: linear-gradient(135deg, #0F1732 5%, #0A0B21 30%);
  border: 1px solid #13234E;
  backdrop-filter: blur(30px);
  -webkit-backdrop-filter: blur(30px);
  box-shadow: 
    inset 0 1px 1px rgba(255, 255, 255, 0.05);

  h3 {
    font-size: 14px;
    font-weight: 500;
    color: $text-secondary;
    margin: 0 0 16px 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .controls-row {
    display: flex;
    gap: 12px;
    margin-bottom: 16px;
    flex-wrap: wrap;

    .control-part {
      flex: 1;
      min-width: 150px;
      display: flex;
      align-items: center;
      gap: 8px;
      background: transparent;
      border: 1px solid #13234E;
      border-radius: 12px;
      padding: 8px;
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);

      .status-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        background: transparent;
        border: none;
        color: white;
        border-radius: 8px;
        padding: 10px;
        font-weight: 500;
        font-size: 16px;
        cursor: pointer;
        transition: all 0.3s ease;
        flex: 1;

        &:hover:not(:disabled) {
          background: $button-hover-bg;
          transform: translateY(-1px);
        }

        &:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        i {
          font-size: 16px;
          margin-right: 8px;
          color: #81B9E2;
        }
      }

      .status-btn-text {
        width: 50px;
        height: 30px;
        font-size: 16px;
        color: $text-secondary;
        font-weight: 500;
        background: transparent;
        border: 1px solid #13234E;
        border-radius: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }

    &:last-child {
      margin-bottom: 0;
    }
  }
}

// .status-btn {
//   background: rgba(30, 41, 82, 0.4);
//   border: 1px solid rgba(59, 130, 246, 0.2);
//   color: $text-secondary;
//   border-radius: 8px;
//   padding: 10px 16px;
//   font-weight: 500;
//   font-size: 14px;
//   cursor: pointer;
//   transition: all 0.3s ease;
//   display: flex;
//   align-items: center;
//   gap: 8px;
//   flex: 1;
//   justify-content: center;
//   backdrop-filter: blur(10px);
//   -webkit-backdrop-filter: blur(10px);

//   &.active {
//     color: #3b82f6;
//     border-color: #3b82f6;
//     background: rgba(59, 130, 246, 0.15);
//     box-shadow: 0 0 20px rgba(59, 130, 246, 0.2);
//   }

//   &:hover:not(:disabled) {
//     background: rgba(59, 130, 246, 0.1);
//     transform: translateY(-1px);
//   }

//   &:disabled {
//     opacity: 0.5;
//     cursor: not-allowed;
//   }

//   i {
//     font-size: 16px;
//   }
// }

.volume-control {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  background: linear-gradient(90deg, #0F1732 30%, #0A0B21 50%);
  border: 1px solid #13234E;
  border-radius: 16px;
  padding: 12px;

  i {
    color: $text-secondary;
    font-size: 16px;
    min-width: 20px;
  }

  .volume-slider-container {
    display: flex;
    align-items: center;
    flex: 1;
    margin: 0 4px;
  }

  .slider {
    width: 100%;
    height: 6px;
    border-radius: 3px;
    background: rgba(59, 130, 246, 0.2);
    outline: none;
    cursor: pointer;
    appearance: none;
    -webkit-appearance: none;
    
    &::-webkit-slider-thumb {
      -webkit-appearance: none;
      width: 18px;
      height: 18px;
      border-radius: 50%;
      background: linear-gradient(135deg, #3b82f6, #60a5fa);
      cursor: pointer;
      border: 3px solid $bg-color;
      transition: all 0.2s ease;
      box-shadow: 0 2px 8px rgba(59, 130, 246, 0.4);
      
      &:hover {
        transform: scale(1.1);
        box-shadow: 0 4px 12px rgba(59, 130, 246, 0.6);
      }
    }
    
    &::-moz-range-thumb {
      width: 18px;
      height: 18px;
      border-radius: 50%;
      background: linear-gradient(135deg, #3b82f6, #60a5fa);
      cursor: pointer;
      border: none;
      box-shadow: 0 2px 8px rgba(59, 130, 246, 0.4);
    }
  }

  .volume-value {
    font-size: 14px;
    color: $text-secondary;
    font-weight: 500;
    min-width: 35px;
    text-align: right;
  }
}

.spatial-mode-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  i {
    color: $text-secondary;
    font-size: 16px;
  }

  span {
    font-size: 14px;
    color: $text-secondary;
    font-weight: 500;
  }

  .toggle-switch-container {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .toggle-switch {
    width: 48px;
    height: 22px;
    background: rgba(30, 41, 82, 0.6);
    border-radius: 14px;
    padding: 2px;
    cursor: pointer;
    transition: all 0.3s ease;
    position: relative;
    border: 1px solid rgba(59, 130, 246, 0.2);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    display: flex;
    align-items: center;

    &.active {
      background: #126B67;
      border-color: $primary-accent;
      box-shadow: 0 0 20px rgba(0, 217, 163, 0.3);
    }

    &::before {
      content: '';
      position: absolute;
      top: 2px;
      left: 2px;
      width: 22px;
      height: 22px;
      background: linear-gradient(135deg, #ffffff, #f8fafc);
      border-radius: 50%;
      transition: all 0.3s ease;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    }

    &.active::before {
      transform: translateX(24px);
      background: #5EFAE7; // Orange clair pour le curseur AFK
    }
  }

  .toggle-status-container {
    display: flex;
    align-items: center;
    gap: 15px;
  }

  .toggle-status {
    font-size: 14px;
    color: $primary-accent;
    font-weight: 600;
    text-transform: uppercase;
  }
}

.users-list {
  //background: rgba(10, 12, 28, 0.4);
  border-radius: 16px;
  padding: 20px;
  background: linear-gradient(100deg, #0F1732 0%, #0A0B21 30%);
  border: 1px solid #13234E;
  backdrop-filter: blur(30px);
  -webkit-backdrop-filter: blur(30px);
  box-shadow: 
    inset 0 1px 1px rgba(255, 255, 255, 0.05);

  h3 {
    font-size: 14px;
    font-weight: 500;
    color: $text-secondary;
    margin: 0 0 16px 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .users-grid {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
}

.user-item {
  display: flex;
  align-items: center;
  padding: 12px;
  border-radius: 12px;
  //background: rgba(30, 41, 82, 0.4);
  background: linear-gradient(100deg, #0F1732 0%, #0A0B21 30%);
  border: 1px solid #13234E;
  transition: all 0.3s ease;
  position: relative;
  
  &:hover {
    background: rgba(59, 130, 246, 0.1);
    border-color: rgba(59, 130, 246, 0.2);
  }
  
  &.speaking {
    border-color: #3b82f6;
    background: rgba(59, 130, 246, 0.15);
    animation: speaking-pulse 1.5s ease-in-out infinite;
  }

  .user-status {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-left: auto;
    min-width: 20px;
        
    i {
      font-size: 18px;
      
      &.fa-user-astronaut {
        color: #dc2626; // Orange pour AFK
      }
      
      &.fa-microphone-slash {
        color: #dc2626; // Rouge pour micro coupé
      }
      
      &.fa-volume-mute {
        color: #dc2626; // Rouge foncé pour audio coupé
      }
    }
  }
  
  .user-avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    //background: $secondary-accent;
    background: linear-gradient(180deg, #574ECF, #393FBE);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 12px;
    box-shadow: 0 2px 8px rgba(124, 58, 237, 0.3);
    border: 1px solid #474EF4;
    
    i {
      color: white;
      font-size: 18px;
    }
  }
  
  .user-info {
    flex: 1;
    width: 50%;
    
    .user-name {
      font-weight: 600;
      font-size: 15px;
      color: $text-primary;
      display: flex;
      align-items: center;
      gap: 6px;

      .you-tag {
        font-size: 11px;
        font-weight: 500;
        color: $text-secondary;
      }
    }
    
    .user-time {
      font-size: 12px;
      color: $text-secondary;
      font-weight: 400;
    }
  }
}

.connection-controls {
    width: fit-content;
    display: flex;
    gap: 20px;
    margin: 0 auto;
  text-align: center;
  
  button {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 15px 30px;
    border: none;
    border-radius: 10px;
    font-size: 1.1rem;
    font-weight: bold;
    cursor: pointer;
    transition: all 0.3s ease;
    
    &:disabled {
      opacity: 0.7;
      cursor: not-allowed;
    }
  }
  
  .join-button {
    background: linear-gradient(45deg, $accent-color, #a29bfe);
    color: white;
    
    &:hover:not(:disabled) {
      transform: translateY(-3px);
      box-shadow: 0 10px 20px rgba(108, 92, 231, 0.3);
    }
  }
  
  .leave-button {
    background: $error-color;
    color: white;
    
    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 10px 20px rgba(225, 112, 85, 0.3);
    }
  }
}

.notifications {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 1000;
  
  .notification {
    background: $back-color;
    color: white;
    padding: 12px 20px;
    border-radius: 8px;
    margin-bottom: 10px;
    border-left: 4px solid;
    animation: slideIn 0.3s ease;
    
    &.success {
      border-left-color: $success-color;
    }
    
    &.error {
      border-left-color: $error-color;
    }
    
    &.warning {
      border-left-color: $warning-color;
    }
    
    &.info {
      border-left-color: $accent-color;
    }
  }
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes speaking-pulse {
  0% {
    border-color: #3b82f6;
    background: rgba(59, 130, 246, 0.15);
  }
  50% {
    border-color: #60a5fa;
    background: rgba(59, 130, 246, 0.25);
  }
  100% {
    border-color: #3b82f6;
    background: rgba(59, 130, 246, 0.15);
  }
}

// ========== RESPONSIVE MOBILE ==========
@media (max-width: 768px) {
  .orbit-container {
    padding-bottom: 20px;
  }
  
  .orbit-header {
    flex-direction: column;
    gap: 15px;
    padding: 20px 3%;
    
    .orbit-title {
      text-align: center;
      
      h1 {
        font-size: 2.2rem;
      }
      
      p {
        font-size: 1rem;
      }
    }
    
    .orbit-status {
      width: 100%;
      text-align: center;
      
      span {
        display: inline-block;
        padding: 10px 20px;
        font-size: 0.9rem;
      }
    }
  }
  
  .orbit-main {
    max-width: 90%;
    padding: 20px 10px;
  }
  
  .audio-controls {
    flex-direction: column;
    gap: 15px;
    padding: 15px;
    
    button {
      width: 100%;
      justify-content: center;
      padding: 15px 20px;
      font-size: 1rem;
      
      i {
        margin-right: 8px;
      }
    }
    
    .volume-control {
      width: 100%;
      justify-content: center;
      flex-wrap: wrap;
      gap: 8px;
      
      input[type="range"] {
        width: 200px;
        flex: 1;
        min-width: 150px;
      }
      
      .volume-text {
        min-width: 50px;
        font-size: 1rem;
      }
    }
  }
  
  .users-list {
    padding: 15px;
    
    h3 {
      font-size: 1.2rem;
      margin-bottom: 15px;
    }
    
    .users-grid {
      grid-template-columns: 1fr;
      gap: 10px;
    }
    
    .user-card {
      padding: 12px;
      flex-wrap: wrap;
      
      .user-avatar {
        width: 35px;
        height: 35px;
        
        i {
          font-size: 16px;
        }
      }
      
      .user-info {
        flex: 1;
        min-width: 120px;
        
        .user-name {
          font-size: 0.95rem;
          margin-bottom: 2px;
        }
        
        .join-time {
          font-size: 0.8rem;
        }
        
        .user-states {
          margin-top: 6px;
          
          .state-icon {
            width: 14px;
            height: 14px;
            font-size: 9px;
          }
        }
      }
    }
  }
  
  .connection-controls {
    flex-direction: column;
    width: 100%;
    gap: 15px;
    
    button {
      width: 100%;
      padding: 18px 25px;
      font-size: 1.1rem;
      justify-content: center;
      
      i {
        margin-right: 10px;
      }
    }
  }
  
  .notifications {
    top: 10px;
    right: 10px;
    left: 10px;
    
    .notification {
      padding: 15px;
      font-size: 0.9rem;
      margin-bottom: 8px;
    }
  }
}

// ========== RESPONSIVE TRÈS PETIT MOBILE ==========
@media (max-width: 480px) {
  .orbit-header {
    padding: 15px 2%;
    
    .orbit-title {
      h1 {
        font-size: 1.8rem;
      }
      
      p {
        font-size: 0.9rem;
      }
    }
  }
  
  .orbit-main {
    padding: 15px 5px;
  }
  
  .audio-controls {
    padding: 12px;
    gap: 12px;
    
    button {
      padding: 12px 15px;
      font-size: 0.9rem;
    }
    
    .volume-control {
      input[type="range"] {
        width: 150px;
        min-width: 120px;
      }
      
      .volume-text {
        font-size: 0.9rem;
      }
    }
  }
  
  .users-list {
    padding: 12px;
    
    .user-card {
      padding: 10px;
      gap: 8px;
      
      .user-avatar {
        width: 30px;
        height: 30px;
        
        i {
          font-size: 14px;
        }
      }
      
      .user-info {
        .user-name {
          font-size: 0.9rem;
        }
        
        .join-time {
          font-size: 0.75rem;
        }
        
        .user-states .state-icon {
          width: 12px;
          height: 12px;
          font-size: 8px;
        }
      }
    }
  }
  
  .connection-controls {
    gap: 12px;
    
    button {
      padding: 15px 20px;
      font-size: 1rem;
    }
  }
}

// ========== RESPONSIVE PAYSAGE MOBILE ==========
@media (max-width: 768px) and (orientation: landscape) {
  .orbit-header {
    flex-direction: row;
    padding: 15px 3%;
    
    .orbit-title {
      text-align: left;
      
      h1 {
        font-size: 2rem;
      }
    }
    
    .orbit-status {
      width: auto;
      text-align: right;
    }
  }
  
  .audio-controls {
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
    
    button {
      width: auto;
      min-width: 140px;
      flex: 1;
      max-width: 200px;
    }
    
    .volume-control {
      width: 100%;
      order: 1;
      margin-top: 10px;
    }
  }
  
  .users-grid {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  }
  
  .connection-controls {
    flex-direction: row;
    justify-content: center;
    
    button {
      width: auto;
      min-width: 200px;
    }
  }
}
</style>
